/**
 * Eigenkontrolle des Betreibers im Kundenportal.
 *
 * Nach DIN EN 1176-7 ist die regelmäßige Kontrolle Pflicht des Betreibers,
 * nicht des Prüfbüros. Hier führt er sie und bekommt seinen Nachweis.
 *
 * Der Ton ist deshalb ein anderer als im Prüfbogen: keine Mängelstufen,
 * keine Normverweise, kein Fachjargon. Der Bauhofmitarbeiter geht mit dem
 * Handy über den Platz und sagt zu jedem Punkt „in Ordnung" oder schreibt
 * hin, was er sieht. Was daraus folgt, entscheidet der Prüfer.
 */

import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/klient'
import { Fehlerhinweis, Ladeanzeige, Leerhinweis, datumDeutsch } from '../bausteine/Hinweise'

type Kontrollart = 'sicht' | 'operativ'

export const ARTTEXT: Record<Kontrollart, string> = {
  sicht: 'Visuelle Routinekontrolle',
  operativ: 'Operative Inspektion',
}

type Punkt = {
  spielgeraet_id: string | null
  bezeichnung: string
  zusatz: string | null
  sortierung: number
}

type Platz = {
  spielplatz_id: string
  name: string
  ort: string | null
  geraete: Punkt[]
  intervall_tage: number | null
  letzte_kontrolle_am: string | null
  faellig_am: string | null
  ueberfaellig: boolean
}

type Gang = {
  id: string
  spielplatz_name: string
  art: Kontrollart
  datum: string
  durchgefuehrt_von: string
  bemerkung: string | null
  hat_befund: boolean
  gesehen_am: string | null
  befunde: {
    id: string
    bezeichnung: string
    in_ordnung: boolean
    befund: string | null
    foto_ids: string[]
  }[]
}

type Befugnis = { befugnis: string; arten: Kontrollart[]; fotos: boolean }

export function Eigenkontrolle() {
  const [befugnis, setzeBefugnis] = useState<Befugnis | null>(null)
  const [plaetze, setzePlaetze] = useState<Platz[]>([])
  const [gaenge, setzeGaenge] = useState<Gang[]>([])
  const [laeuftFuer, setzeLaeuftFuer] = useState<Platz | null>(null)
  const [fotosFuer, setzeFotosFuer] = useState<Gang | null>(null)
  const [fehler, setzeFehler] = useState<unknown>(null)

  const laden = useCallback(async () => {
    try {
      const b = await api.holen<Befugnis>('/api/portal/eigenkontrolle/befugnis')
      setzeBefugnis(b)
      if (b.arten.length > 0) {
        setzePlaetze(await api.holen<Platz[]>('/api/portal/eigenkontrolle/plaetze'))
        setzeGaenge(await api.holen<Gang[]>('/api/portal/eigenkontrolle/gaenge'))
      }
    } catch (f) {
      setzeFehler(f)
    }
  }, [])

  useEffect(() => {
    void laden()
  }, [laden])

  if (fehler) return <Fehlerhinweis fehler={fehler} />
  if (!befugnis) return <Ladeanzeige />

  // Ohne Freigabe erscheint der Abschnitt gar nicht. Ein Knopf, den der
  // Server anschließend ablehnt, ist schlimmer als kein Knopf.
  if (befugnis.arten.length === 0) return null

  if (laeuftFuer) {
    return (
      <Kontrollgang
        platz={laeuftFuer}
        arten={befugnis.arten}
        abbrechen={() => setzeLaeuftFuer(null)}
        fertig={async (neu) => {
          setzeLaeuftFuer(null)
          // Fotos erst nach dem Eintragen: vorher gibt es den Gang noch
          // nicht, an den sie gehören.
          if (befugnis.fotos && neu?.hat_befund) setzeFotosFuer(neu)
          await laden()
        }}
      />
    )
  }

  if (fotosFuer) {
    return (
      <Fotonachtrag
        gang={fotosFuer}
        fertig={async () => {
          setzeFotosFuer(null)
          await laden()
        }}
      />
    )
  }

  return (
    <section>
      <h2 className="ueberschrift-akzent mb-3 text-xl">Eigene Kontrolle</h2>
      <p className="mb-3 text-base text-slate-700">
        Die regelmäßige Kontrolle Ihrer Spielplätze obliegt Ihnen als Betreiber
        (DIN EN 1176-7). Tragen Sie sie hier ein — Sie erhalten einen Nachweis
        zum Abheften, und wir sehen, was Ihnen aufgefallen ist.
      </p>

      <ul className="space-y-2">
        {plaetze.map((p) => (
          <li key={p.spielplatz_id} className="karte flex flex-wrap items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="font-semibold">{p.name}</div>
              <div className="text-base text-slate-700">
                {p.letzte_kontrolle_am
                  ? `Zuletzt kontrolliert am ${datumDeutsch(p.letzte_kontrolle_am)}`
                  : 'Noch keine Kontrolle eingetragen'}
                {p.intervall_tage ? ` · alle ${p.intervall_tage} Tage` : ''}
              </div>
              {p.ueberfaellig && (
                <div className="mt-1 font-semibold text-stufe-2">
                  Kontrolle steht an{p.faellig_am ? ` seit ${datumDeutsch(p.faellig_am)}` : ''}
                </div>
              )}
            </div>
            <button
              type="button"
              className={p.ueberfaellig ? 'knopf-haupt px-3' : 'knopf-zweit px-3'}
              onClick={() => setzeLaeuftFuer(p)}
            >
              Kontrolle eintragen
            </button>
          </li>
        ))}
      </ul>

      <h3 className="mb-2 mt-6 text-lg font-bold">Bisherige Kontrollen</h3>
      {gaenge.length === 0 ? (
        <Leerhinweis
          titel="Noch keine Kontrolle"
          text="Eingetragene Kontrollen erscheinen hier mit Nachweis zum Herunterladen."
        />
      ) : (
        <ul className="space-y-2">
          {gaenge.map((g) => (
            <li key={g.id} className="karte flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-semibold">
                  {datumDeutsch(g.datum)} · {g.spielplatz_name}
                </div>
                <div className="text-base text-slate-700">
                  {ARTTEXT[g.art]} · {g.durchgefuehrt_von}
                </div>
                {g.hat_befund ? (
                  <div className="mt-1 text-base font-semibold text-stufe-2">
                    {g.befunde.filter((b) => !b.in_ordnung).length} Beanstandung(en)
                    {g.gesehen_am
                      ? ` · vom Prüfbüro gesehen am ${datumDeutsch(g.gesehen_am)}`
                      : ' · dem Prüfbüro gemeldet'}
                  </div>
                ) : (
                  <div className="mt-1 text-base text-stufe-ob">Keine Beanstandung</div>
                )}
              </div>
              <a
                className="knopf-zweit px-3"
                href={`/api/portal/eigenkontrolle/${g.id}.pdf`}
                target="_blank"
                rel="noreferrer"
              >
                Nachweis
              </a>
              {g.befunde.some((b) => b.foto_ids.length > 0) && (
                <div className="basis-full">
                  <div className="mt-2 flex flex-wrap gap-2">
                    {g.befunde.flatMap((b) =>
                      b.foto_ids.map((id) => (
                        <img
                          key={id}
                          src={`/api/portal/eigenkontrolle/fotos/${id}`}
                          alt={`Foto zu ${b.bezeichnung}`}
                          className="h-20 w-28 rounded border-2 border-slate-200 object-cover"
                          loading="lazy"
                        />
                      )),
                    )}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * Der Gang über den Platz.
 *
 * Voreinstellung ist „in Ordnung" für jeden Punkt. Das ist der Regelfall,
 * und wer dreißig Geräte einzeln bestätigen müsste, hakt irgendwann alles
 * durch, ohne hinzusehen. Wer etwas findet, tippt auf den Punkt und
 * schreibt es hin — das ist die Ausnahme und darf Arbeit machen.
 */
function Kontrollgang({
  platz,
  arten,
  abbrechen,
  fertig,
}: {
  platz: Platz
  arten: Kontrollart[]
  abbrechen: () => void
  fertig: (neu?: Gang) => Promise<void>
}) {
  const [art, setzeArt] = useState<Kontrollart>(arten[0])
  const [datum, setzeDatum] = useState(new Date().toISOString().slice(0, 10))
  const [bemerkung, setzeBemerkung] = useState('')
  const [befunde, setzeBefunde] = useState<Record<string, string>>({})
  const [laeuft, setzeLaeuft] = useState(false)
  const [fehler, setzeFehler] = useState<unknown>(null)

  // Der Schlüssel ist die Gerätekennung, für den Platz selbst ein fester Wert.
  const schluessel = (p: Punkt) => p.spielgeraet_id ?? 'platz'
  const beanstandet = Object.values(befunde).filter((t) => t.trim()).length

  async function senden() {
    setzeFehler(null)
    setzeLaeuft(true)
    try {
      const neu = await api.senden<Gang>('/api/portal/eigenkontrolle', {
        spielplatz_id: platz.spielplatz_id,
        art,
        datum,
        bemerkung: bemerkung.trim() || null,
        punkte: platz.geraete.map((p) => {
          const text = (befunde[schluessel(p)] ?? '').trim()
          return {
            spielgeraet_id: p.spielgeraet_id,
            bezeichnung: p.bezeichnung,
            in_ordnung: text === '',
            befund: text || null,
            sortierung: p.sortierung,
          }
        }),
      })
      await fertig(neu)
    } catch (f) {
      setzeFehler(f)
    } finally {
      setzeLaeuft(false)
    }
  }

  return (
    <section className="space-y-4">
      <header>
        <h2 className="text-xl font-bold">Kontrolle: {platz.name}</h2>
        <p className="text-base text-slate-700">
          Alles gilt als in Ordnung, solange Sie nichts hineinschreiben. Tragen
          Sie nur ein, was Ihnen auffällt.
        </p>
      </header>

      <div className="grid gap-3 sm:grid-cols-2">
        {arten.length > 1 && (
          <div>
            <label htmlFor="k-art">Art der Kontrolle</label>
            <select
              id="k-art"
              value={art}
              onChange={(e) => setzeArt(e.target.value as Kontrollart)}
            >
              {arten.map((a) => (
                <option key={a} value={a}>
                  {ARTTEXT[a]}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label htmlFor="k-datum">Datum</label>
          <input
            id="k-datum"
            type="date"
            value={datum}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setzeDatum(e.target.value)}
          />
        </div>
      </div>

      <ul className="space-y-2">
        {platz.geraete.map((p) => {
          const wert = befunde[schluessel(p)] ?? ''
          const offen = wert.trim() !== ''
          return (
            <li
              key={schluessel(p)}
              className={`karte ${offen ? 'border-stufe-2' : ''}`}
            >
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{p.bezeichnung}</div>
                  {p.zusatz && (
                    <div className="text-base text-slate-600">{p.zusatz}</div>
                  )}
                </div>
                {!offen && (
                  <span className="marke bg-stufe-ob-grund text-stufe-ob">in Ordnung</span>
                )}
              </div>

              <label htmlFor={`b-${schluessel(p)}`} className="nur-vorlesen">
                Was ist Ihnen an {p.bezeichnung} aufgefallen?
              </label>
              <textarea
                id={`b-${schluessel(p)}`}
                className="mt-2"
                rows={offen ? 2 : 1}
                value={wert}
                placeholder="Nichts eintragen, wenn alles in Ordnung ist"
                onChange={(e) =>
                  setzeBefunde((alt) => ({ ...alt, [schluessel(p)]: e.target.value }))
                }
              />
            </li>
          )
        })}
      </ul>

      <div>
        <label htmlFor="k-bemerkung">Bemerkung zum ganzen Gang</label>
        <textarea
          id="k-bemerkung"
          rows={2}
          value={bemerkung}
          onChange={(e) => setzeBemerkung(e.target.value)}
          placeholder="Zum Beispiel: nach dem Sturm nachgesehen."
        />
      </div>

      <Fehlerhinweis fehler={fehler} />

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="knopf-haupt"
          disabled={laeuft}
          onClick={() => void senden()}
        >
          {laeuft ? 'Speichert …' : 'Kontrolle abschließen'}
        </button>
        <button type="button" className="knopf-still" onClick={abbrechen}>
          Abbrechen
        </button>
        <span className="text-base text-slate-700">
          {beanstandet === 0
            ? 'Keine Beanstandung'
            : `${beanstandet} Beanstandung(en) — wird dem Prüfbüro gemeldet`}
        </span>
      </div>
    </section>
  )
}

/**
 * Fotos zu den Beanstandungen nachreichen.
 *
 * Nach dem Eintragen und nicht davor: vorher gibt es den Kontrollgang
 * noch nicht, an den die Bilder gehören. Das ist der einzige Schönheits-
 * fehler an dieser Reihenfolge — dafür kann nichts halb gespeichert
 * liegenbleiben.
 *
 * Nur zu Beanstandungen. Ein Bild von einem Gerät, an dem nichts ist,
 * kostet Platz und sagt niemandem etwas.
 */
function Fotonachtrag({
  gang,
  fertig,
}: {
  gang: Gang
  fertig: () => Promise<void>
}) {
  const [stand, setzeStand] = useState(gang)
  const [laeuft, setzeLaeuft] = useState<string | null>(null)
  const [fehler, setzeFehler] = useState<unknown>(null)

  const beanstandet = stand.befunde.filter((b) => !b.in_ordnung)

  async function hochladen(befundId: string, datei: File) {
    setzeFehler(null)
    setzeLaeuft(befundId)
    try {
      const formular = new FormData()
      formular.append('datei', datei)
      formular.append('befund_id', befundId)
      await api.hochladen(`/api/portal/eigenkontrolle/${gang.id}/fotos`, formular)
      // Den Gang neu holen, damit die Bilder erscheinen.
      const gaenge = await api.holen<Gang[]>('/api/portal/eigenkontrolle/gaenge')
      const frisch = gaenge.find((g) => g.id === gang.id)
      if (frisch) setzeStand(frisch)
    } catch (f) {
      setzeFehler(f)
    } finally {
      setzeLaeuft(null)
    }
  }

  return (
    <section className="space-y-4">
      <header>
        <h2 className="text-xl font-bold">Kontrolle eingetragen</h2>
        <p className="text-base text-slate-700">
          Fotos zu den Beanstandungen helfen dem Prüfbüro sehr — ohne sie muss
          jemand hinfahren, um überhaupt zu sehen, worum es geht.
        </p>
      </header>

      <Fehlerhinweis fehler={fehler} />

      <ul className="space-y-2">
        {beanstandet.map((b) => (
          <li key={b.id} className="karte border-stufe-2">
            <div className="font-semibold">{b.bezeichnung}</div>
            <div className="text-base text-slate-700">{b.befund}</div>

            {b.foto_ids.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {b.foto_ids.map((id) => (
                  <img
                    key={id}
                    src={`/api/portal/eigenkontrolle/fotos/${id}`}
                    alt=""
                    className="h-20 w-28 rounded border-2 border-slate-200 object-cover"
                  />
                ))}
              </div>
            )}

            <label className="knopf-zweit mt-2 inline-block cursor-pointer px-3">
              {laeuft === b.id ? 'Lädt …' : 'Foto aufnehmen'}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                disabled={laeuft !== null}
                onChange={(e) => {
                  const datei = e.target.files?.[0]
                  // Das Feld leeren, sonst löst dasselbe Bild kein zweites
                  // Mal aus.
                  e.target.value = ''
                  if (datei) void hochladen(b.id, datei)
                }}
              />
            </label>
          </li>
        ))}
      </ul>

      <button type="button" className="knopf-haupt" onClick={() => void fertig()}>
        Fertig
      </button>
    </section>
  )
}
